<script>
  import {
    getFloodLevelsConfig,
    getGaugeStageFlowLinesLabels,
    getGaugeData,
    getLatestObservedValues,
    getLegendSections,
    getMetaDataSubHeading,
    getRecordStageLine,
    getRecordStageLineConfig,
    getXAxisExtremes,
    obsForecastTopBar,
    parseCrestsData,
    parseLowWaterData,
    verifyDataExists,
    transformAllStreamFlows,
  } from './base.js'

  import {
    addLowwaterShading,
    adjustYAxisTicks,
    calculateTicks,
    cfsToKcfs,
    chartHasNoData,
    chartWaterMark,
    checkRatings,
    fetchWithTimeout,
    getChartPositionsByData,
    getFloodCategories,
    getFlowUnits,
    getFlowUnitsByConfig,
    getStageflowExtremes,
    getStreamflowExtremes,
    getLowWaterStageLine,
    getMinMaxSeriesDataYaxis,
    getNormalPoolFloodThreshHold,
    getNormalPoolFloodThreshHoldConfig,
    getPlotLinesFlowBandsConfig,
    getPrecision,
    getTitleText,
    getYAxisExtremes,
    isFalsy,
    isMissing,
    kcfsToCfs,
    kcfsToCfsString,
    latestObservedBox,
    parseFloodCats,
    preventYaxisTickOverlap,
    processUnderTwoNumbers,
    ratingsLookup,
    seriesStyle,
    toFixedPos,
    addNOAAWaterMark,
    imageResize,
  } from '../../common/hydrographHelper.js'

  import { refresh } from './store.js'

  // With "ons-dev" configuration this
  // will allow debugging.
  //const DEBUG = true
  const DEBUG = false

  import dayjs from 'dayjs'
  import tz from 'dayjs/plugin/timezone'
  import utc from 'dayjs/plugin/utc'
  import adv from 'dayjs/plugin/advancedFormat'
  import isBetween from 'dayjs/plugin/isBetween'
  import relativeTime from 'dayjs/plugin/relativeTime'

  dayjs.extend(tz)
  dayjs.extend(utc)
  dayjs.extend(adv)
  dayjs.extend(isBetween)
  dayjs.extend(relativeTime)

  //import Highcharts from 'highcharts'
  import Highcharts, { isObject } from 'highcharts/highstock'

  // loadingStatus gives a visual update to the front-end user as to the status of the many back-end AJAX promises.
  //  0: hasn't loaded
  //  200: success
  //  404: failed
  let loadingStatus = {
    analysis_assimilation: 0,
    short_range: 0,
    long_range: 0,
    medium_range: 0,
    medium_range_blend: 0,
    // 'gaugeConfig': 0,
  }

  // Wrap the renderSplit method to overwrite the tooltip fill color based on series color. TM
  Highcharts.wrap(Highcharts.Tooltip.prototype, 'renderSplit', function (p, labels, points) {
    p.call(this, labels, points)
    // This chunk of code will override the bottom tooltip on the xAxis.
    // Documented it here but not used; use the tooltip.backgroundColor property instead.
    /* let tt = this.tt

    if (tt) {
      tt.attr({
        fill: '#FFC0CB'
      })
    } */

    if (points) {
      points.forEach((point) => {
        let s = point.series
        if (s.tt) {
          s.tt.attr({
            fill: s.color,
          })
        }
      })
    }
  })

  // Generic plugin that adds support for listening to container resize rather than
  // window resize. As of 2018 only Chrome supports ResizeObserver.
  Highcharts.wrap(Highcharts.Chart.prototype, 'setReflow', function (proceed, reflow) {
    var chart = this
    proceed.call(this, reflow)

    if (reflow !== false && typeof ResizeObserver === 'function') {
      // Unbind window.onresize handler so we don't do double redraws
      if (this.unbindReflow) {
        this.unbindReflow()
      }

      var ro = new ResizeObserver(function () {
        chart.reflow()
      })

      ro.observe(this.renderTo)
    }
  })

  // Extensions in Highcharts NPM package
  import data from 'highcharts/modules/data'
  import exporting from 'highcharts/modules/exporting'
  import offlineExporting from 'highcharts/modules/offline-exporting'
  import noData from 'highcharts/modules/no-data-to-display'

  data(Highcharts)
  exporting(Highcharts)
  offlineExporting(Highcharts)
  noData(Highcharts)

  import Legend from './Legend.svelte'

  export let gauge
  export let stageflow
  export let gaugeConfig // gauge configuration as JSON
  export let assetURL = ''
  export let ratingsCurve
  export let chartName
  export let reachID
  export let view
  export let lid = null // added for Chrome warning

  // Set the background color for both charts - NWM is NatureNurture color
  let chartBgColor = '#DAEAF6'

  // Set the disclaimer
  const disclaimerShown = false

  let chart = {}
  let promiseWait

  let streamflowKeyVals = {
    analysisAssimilation: 'analysis_assimilation',
    shortRange: 'short_range',
    mediumRange: 'medium_range',
    mediumRangeBlend: 'medium_range_blend',
    longRange: 'long_range',
    reach: 'reach',
  }

  let streamflows = [
    'analysis_assimilation',
    'short_range',

    'medium_range_blend',

    'medium_range_ensemble_mean',
    'medium_range_ensemble_member1',
    'medium_range_ensemble_member2',
    'medium_range_ensemble_member3',
    'medium_range_ensemble_member4',
    'medium_range_ensemble_member5',
    'medium_range_ensemble_member6',

    'long_range_ensemble_mean',
    'long_range_ensemble_member1',
    'long_range_ensemble_member2',
    'long_range_ensemble_member3',
    'long_range_ensemble_member4',
  ]
  // This powers the series configs and the custom legend buttons.
  let stageflows = ['observed', 'forecast', 'line_record', 'line_lowwater', 'normal_pool']
  let shortToolTips = {
    Observed: 'OBS',
    Forecast: 'FCST',
    Analysis: 'ANA',
    'Short Range': 'SR',
    'Medium Range Blend': 'MRB',
    'Medium Range Ensemble Mean': 'MRM',
    'Medium Range Member 1': 'MR 1',
    'Medium Range Member 2': 'MR 2',
    'Medium Range Member 3': 'MR 3',
    'Medium Range Member 4': 'MR 4',
    'Medium Range Member 5': 'MR 5',
    'Medium Range Member 6': 'MR 6',
    'Medium Range Member 7': 'MR 7',
    'Long Range Ensemble Mean': 'LRM',
    'Long Range Member 1': 'LR 1',
    'Long Range Member 2': 'LR 2',
    'Long Range Member 3': 'LR 3',
    'Long Range Member 4': 'LR 4',
  }

  let shortLegend = {
    Observed: 'OBS',
    Forecast: 'FCST',
    Analysis: 'ANA',
    'Short Range': 'SR',
    'Medium Range Blend': 'MRB',
    'Medium Range Ensemble Mean': 'MRM',
    'Medium Range Member 1': 'MR 1',
    'Medium Range Member 2': 'MR 2',
    'Medium Range Member 3': 'MR 3',
    'Medium Range Member 4': 'MR 4',
    'Medium Range Member 5': 'MR 5',
    'Medium Range Member 6': 'MR 6',
    'Long Range Ensemble Mean': 'LRM',
    'Long Range Member 1': 'LR 1',
    'Long Range Member 2': 'LR 2',
    'Long Range Member 3': 'LR 3',
    'Long Range Member 4': 'LR 4',
  }

  // Needed for clicking on multiple reaches along with gauge on national map
  // will cause multiple NWM charts with same 'renderTo' identification and
  // not show all of the hydrographs.
  // Example would be lid PRCM8 with reache IDs 25073192, 22976272, 24073192
  let renderTo = null
  if (gauge) {
    renderTo = `container-nwm-${gauge.reachID}`
  } else {
    renderTo = `container-nwm-${reachID}`
  }

  // Setup series object and get Gauge info
  let series = {
    now: dayjs(),
    y0: {
      name: 'Stage',
      units: null,
      seriesData: {
        observed: null,
        forecast: null,
      },
    },
    y1: {
      name: 'Flow',
      units: null,
      seriesData: {
        nwm: null,
      },
    },
    labels: {
      observed: {},
      forecast: {},
    },
    obsDataPri: [],
    obsDataSec: [],
    fcstDataPri: [],
    fcstDataSec: [],
    configs: [],
    dataExists: false,
    reachID: reachID && !gauge ? reachID : null,
    floodcategories: null,
    gauge: {},
    hidden: null,
    errors: [],
    ticks: [],
    defaultRawData: [],
    y0Enabled: false,
    peaks: {},
    rawData: [],
    nwmRawData: [],
    wholes: [],
    observedIssuedTime: null,
    forecastIssuedTime: null,
    flowUnitsByCMS: null,
    headRoom: null,
    margin: null,
    footRoom: null,
    precision: 3,
    tenPercent: 0,
  }

  // Verify if we have actual data
  series.dataExists = verifyDataExists(gauge, reachID)
  series.gauge = getGaugeData(series, gauge, series.now)

  Highcharts.setOptions({
    time: {
      useUTC: series.timeZone != 'UTC' ? false : true,
    },
    lang: {
      decimalPoint: '.',
      thousandsSep: ',',
      numericSymbols: null, // otherwise by default ['k', 'M', 'G', 'T', 'P', 'E']
    },
    chartOptions: {
      chart: {
        width: 1700,
        height: 1200,
      },
    },
  })

  series['version'] = {
    view: view,
    chart: 'nwm',
    chartName: chartName,
    highcharts: Highcharts.version,
    container: assetURL,
    renderTo: renderTo,
  }

  // extremes
  series['extremes'] = {
    yaxis: {
      1: { min: null, max: null },
    },
    xaxis: {
      0: { min: null, max: null },
    },
  }

  series['nwm'] = { originals: {} }
  series['ensembles'] = {}

  /**
   * sorts series.rawData object
   * @param d object
   * @returns object
   */
  function sortRawDataSecondaryPrimary(d) {
    if (!d['rawData'] || !d.rawData.length) {
      return {}
    }
    return [...series.rawData].sort((a, b) => {
      if (a.primary && a.secondary === b.secondary) {
        return a.primary - b.primary
      }
      return a.secondary - b.secondary
    })
  }

  /**
   * sorts rawData objects
   * @param d object
   * @returns object
   */
  function sortPrimarySecondary(d) {
    if (!d['rawData'] || !d.rawData.length) {
      return {}
    }
    return [...series.rawData].sort((a, b) => {
      if (a.primary && a.secondary === b.secondary) {
        return a.primary - b.primary
      }
      return a.secondary - b.secondary
    })
  }

  async function getStreamflow(type) {
    let url
    // No data; do not run a fetch
    if (!reachID && !gauge) {
      return
    }
    if (!reachID && gauge && gauge.reachId) {
      reachID = gauge.reachId
    }
    try {
      url = `${OWP_API_URL}${GAUGE_API_RESOURCE}reaches/${reachID}/streamflow?series=${type}`
      let response = await fetchWithTimeout(url, {
        // milliseconds
        //timeout: 30000,
      })
      let json = await response.json()
      if (response.ok) {
        if (json) {
          for (let [key, val] of Object.entries(streamflowKeyVals)) {
            series.nwm.originals[val] = json[key]
            transformAllStreamFlows(series, val, json[key])
          }
        }
        loadingStatus[type] = 200
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        // console.error(`abort for url ${url}:`, err)
        console.error('AbortError raised.')
      }
      console.error(`getStreamflow() catch URL: ${url} -- error object:`, err)
      /*
      UIkit.notification({
        message: `Error: getStreamflow ajax call, url ${url}: `+err,
        status: 'danger',
        timeout: 9000,
      })  
      */

      loadingStatus[type] = 404
    }
  }

  promiseWait = Promise.all([
    getStreamflow('analysis_assimilation'),
    getStreamflow('short_range'),
    getStreamflow('long_range'),
    getStreamflow('medium_range'),
    getStreamflow('medium_range_blend'),
    gaugeConfig,
  ]).then(() => {
    // Setup ratings from ratings curve data.
    if (series.dataExists && ratingsCurve && ratingsCurve.data && ratingsCurve.data.length) {
      series['ratings'] = [...ratingsCurve.data]
    }
    series['ratingsCurve'] = ratingsCurve

    if (checkRatings(series.ratings)) {
      series.extremes['ratings'] = {}
      series.extremes.ratings['stage'] = {}
      series.extremes.ratings.stage['min'] = Math.min.apply(
        null,
        series.ratings.map((i) => i.stage)
      )
      series.extremes.ratings.stage['max'] = Math.max.apply(
        null,
        series.ratings.map((i) => i.stage)
      )
      series.extremes.ratings['flow'] = {}
      series.extremes.ratings.flow['min'] = Math.min.apply(
        null,
        series.ratings.map((i) => i.flow)
      )
      series.extremes.ratings.flow['max'] = Math.max.apply(
        null,
        series.ratings.map((i) => i.flow)
      )
    }

    // Streamflow data. This is done this way for readability... at some point refactor
    series['record_stageline'] = {}

    // Setup the gauge configured flowUnitsByCMS.
    series.flowUnitsByCMS = getFlowUnits(gaugeConfig, 'hydrographNWM')

    // Set up flood categories
    series.floodcategories = getFloodCategories(gauge, series.ratings, series.precision, series.flowUnitsByCMS)

    /**
     * Using data of array objects with x,y values
     * get converted data using the ratings lookup.
     * @param data
     * @param stageflowUnit
     * @param ratings
     */
    function getDataConverted(data, ratings) {
      let ret = []
      data.forEach((item) => {
        ret.push([
          item[0],
          ratingsLookup(ratings, item[1], 'flow', getFlowUnitsByConfig(gaugeConfig && gaugeConfig.hydrographNWM), true),
        ])
      })
      return ret
    }

    /**
     * Convert raw data to CFS if necessary and
     * return array of objects with x,y coordinates.
     * @param data
     * @param obsOrFcst
     * @param primOrSec
     * @param stageflow
     */
    function getData(data, obsOrFcst, primOrSec, stageflow) {
      let ret = [],
        maxPeak = [0, 0] // Peak array definition: [validTime, value]

      data.forEach((item) => {
        // We want milliseconds and not seconds
        // to properly calculate the datetimestamps
        // in JavaScript with Highcharts.
        // https://day.js.org/docs/en/display/unix-timestamp-milliseconds
        let valid = dayjs(item.validTime).valueOf()
        if (
          item[primOrSec] <= -999 ||
          (obsOrFcst === 'observed' && valid >= series.now) ||
          (obsOrFcst === 'forecast' && valid <= series.now)
        )
          return

        if (
          stageflow[obsOrFcst][`${primOrSec}Units`] === 'kcfs' &&
          getFlowUnits(gaugeConfig, 'hydrographNWM') === 'cfs'
        ) {
          ret.push([valid, kcfsToCfs(item[primOrSec]), item.validTime])
        } else {
          ret.push([valid, item[primOrSec], item.validTime])
        }
        // Get the max peak value for this series.
        if (item[primOrSec] > maxPeak[1]) {
          maxPeak = [valid, item[primOrSec]]
        }
      })
      // record the peak value for this series. note we check
      // the series.peaks due to abstraction of checking both primary and secondary
      // with OBS and FCST.
      if (!series.peaks[obsOrFcst]) series.peaks[obsOrFcst] = maxPeak

      // This data needs to be sorted for Highcharts.
      if (ret.length) ret.sort((a, b) => parseFloat(a[0]) - parseFloat(b[0]))
      return ret
    }

    /**
     * Get labels for the data.
     * @param rawData
     * @param data1
     * @param label1
     * @param data2 - if this array is included it will create dual labels.
     * @param label2
     */
    function getLabels(data1, label1, data2 = [], label2 = null) {
      let ret = {}
      data1.forEach((i) => {
        let label = `${i[1]} ${label1}`

        if (data2.length && label2) {
          let found = data2.find((t) => t[0] === i[0])
          if (found) {
            label = `${label} / ${found[1]} ${kcfsToCfsString(
              label2,
              (gaugeConfig && gaugeConfig.hydrographNWM) || false
            )}`
          }
        }
        ret[i[0]] = label
      })
      return ret
    }

    /**
     * Recreate primary, secondary data for ticks.
     * @param {*} primary
     * @param {*} secondary
     * @returns
     */
    function createRawData(primary, secondary, type, ratings) {
      let ret1 = []
      let secs = []

      // Figure out the precision to deal with gauges like HOPI1.
      let prec = primary && primary.length ? getPrecision(primary[0][1]) : null
      if (prec && prec > 1) {
        if (primary[0][1] < 0 && prec > 3) {
          series.precision = 3
        } else {
          series.precision = prec
        }
      }
      primary.forEach((i) => {
        if (secs.includes(i[1])) return false
        let found = secondary.length ? secondary.find((ts) => ts[0] == i[0]) : null
        // Do not include object if we do not have primary and secondary data.
        // It is possible the secondary is missing from the primary due to -999.
        if (found && checkRatings(ratings)) {
          // These are backwards because of secondary is the primary yAxis.
          ret1 = [
            ...ret1,
            {
              primary: Number(found[1].toFixed(series.precision)),
              secondary: Number(i[1].toFixed(series.precision)),
              type: type,
            },
          ]
        } else {
          ret1 = [...ret1, { secondary: Number(i[1].toFixed(series.precision)), type: type }]
        }
        secs = [...secs, i[1]]
      })
      return ret1
    }

    /**
     * Get a unique list of all the primary data values for our ticks
     * Use flatMap to omit the -999 (or less) values
     * @param rawData
     */
    function getUniqValues(rawData) {
      if (!rawData.length) return []

      let ticks = [
        ...new Set(
          rawData.flatMap((item) => {
            if (item.secondary === undefined || item.secondary <= -999) {
              return []
            }
            return item.secondary
          })
        ),
      ].sort((a, b) => parseFloat(a) - parseFloat(b))

      return calculateTicks(ticks, false, series.precision)
    }

    // We check data for -999, -999 and also change to KCFS if needed.
    let hidden = []
    let obsDataPri = []
    let obsDataSec = []
    let fcstDataPri = []
    let fcstDataSec = []

    // Default setting for no ratings.
    series.y0.units = 'ft'

    // Start processing yAxis tick positions here.
    if (stageflow && stageflow.observed && gaugeConfig.hydrographNWM.series.observed.enabled) {
      obsDataPri = getData(stageflow.observed.data, 'observed', 'primary', stageflow)
      obsDataSec = getData(stageflow.observed.data, 'observed', 'secondary', stageflow)
      if ((obsDataPri && obsDataPri.length) || (obsDataSec && obsDataSec.length)) series.labels['observed'] = {}
      series.observedIssuedTime = stageflow.observed.issuedTime

      // Create rawData
      if (obsDataPri && obsDataPri.length && stageflow.observed.primaryUnits === 'ft') {
        // These are backwards because of secondary is the primary yAxis.
        series.rawData = [...createRawData(obsDataSec, obsDataPri, 'obs_sec', series.ratings)]
      } else if (obsDataSec && obsDataSec.length && stageflow.observed.secondaryUnits === 'ft') {
        // These are backwards because of secondary is the primary yAxis.
        series.rawData = [...createRawData(obsDataPri, obsDataSec, 'obs_pri', series.ratings)]
      }
      series.obsDataPri = obsDataPri
      series.obsDataSec = obsDataSec
    }

    if (stageflow && stageflow.forecast && gaugeConfig.hydrographNWM.series.forecast.enabled) {
      fcstDataPri = getData(stageflow.forecast.data, 'forecast', 'primary', stageflow)
      fcstDataSec = getData(stageflow.forecast.data, 'forecast', 'secondary', stageflow)
      if ((fcstDataPri && fcstDataPri.length) || (fcstDataSec && fcstDataSec.length)) series.labels['forecast'] = {}
      series.forecastIssuedTime = stageflow.forecast.issuedTime

      // Create rawData only if we don't have OBS data based rawData array of objects.
      if (fcstDataPri && fcstDataPri.length && stageflow.forecast.primaryUnits === 'ft') {
        // These are backwards because of secondary is the primary yAxis.
        series.rawData = [...series.rawData, ...createRawData(fcstDataSec, fcstDataPri, 'fcst_sec', series.ratings)]
      } else if (fcstDataSec && fcstDataSec.length && stageflow.forecast.secondaryUnits === 'ft') {
        // These are backwards because of secondary is the primary yAxis.
        series.rawData = [...series.rawData, ...createRawData(fcstDataPri, fcstDataSec, 'fcst_pri', series.ratings)]
      }
      series.fcstDataPri = fcstDataPri
      series.fcstDataSec = fcstDataSec
      series.rawData = [
        ...series.rawData.sort((a, b) => {
          if (a.primary && a.secondary === b.secondary) {
            return a.primary - b.primary
          }
          return a.secondary - b.secondary
        }),
      ]
    }

    // Start processing the yAxis for NWM hydrograph.
    // The controlling access will be the secondaryY axis.
    //
    // NOTE: Gauge HAGW1 only has single tick value which
    //       causes an issue with chart. Need solution!!
    //
    if (
      (gaugeConfig && obsDataPri && obsDataPri.length) ||
      (fcstDataPri && fcstDataPri.length) ||
      (obsDataSec && obsDataSec.length) ||
      (fcstDataSec && fcstDataSec.length)
    ) {
      // Set defaults for gauges like ACMW1.
      // This is our controller for the labels using validTime and value.
      series.y0.name = 'Stage'
      series.y1.name = 'Flow'

      // OBS TRANSFORMATION: updated hydrograph logic for 2021-10-27.
      if (stageflow && stageflow.observed && obsDataPri.length && stageflow.observed.primaryUnits === 'ft') {
        series.y0.units = stageflow.observed.primaryUnits
        series.y0.seriesData.observed = [...obsDataSec]
        series.y1.units = kcfsToCfsString(
          stageflow.observed.secondaryUnits,
          (gaugeConfig && gaugeConfig.hydrographNWM) || false
        )
        series.labels.observed = getLabels(obsDataSec, series.y1.units)

        // Show the secondary yAxis.
        if (gaugeConfig.hydrographNWM.secondaryY.enabled && obsDataPri.length && checkRatings(series.ratings) == true) {
          if (stageflow.observed.primaryUnits === 'ft') {
            series.labels.observed = getLabels(obsDataPri, series.y0.units, obsDataSec, series.y1.units)
          } else {
            series.labels.observed = getLabels(obsDataSec, series.y1.units, obsDataPri, series.y0.units)
          }
          series.y0Enabled = true
        }

        // This sets up the fake yAxis[1] tick positions.
        hidden = [...obsDataPri]
      } else if (stageflow && stageflow.observed && obsDataSec.length) {
        // If we don't have OBS primary data then use the secondary to set the controlling axis.
        series.y0.units = stageflow.observed.secondaryUnits
        series.y1.units = kcfsToCfsString(
          stageflow.observed.primaryUnits,
          (gaugeConfig && gaugeConfig.hydrographNWM) || false
        )

        series.y0.seriesData.observed = [...obsDataPri]
        series.labels.observed = getLabels(obsDataSec, series.y1.units)

        // Special: we use the ratings table to do a lookup
        // because we do not have a obsDataPri collection.
        if (gaugeConfig.hydrographNWM.secondaryY.enabled && checkRatings(series.ratings)) {
          if (obsDataPri.length) {
            series.labels.observed = getLabels(obsDataSec, series.y1.units, obsDataPri, series.y0.units)
            series.y0Enabled = true
          } else {
            let stageOrFlow = stageflow.observed.secondaryUnits === 'ft' ? 'stage' : 'flow'
            let converted = getDataConverted(obsDataSec, series.ratings, stageOrFlow)
            series.labels.observed = getLabels(obsDataSec, series.y1.units, converted, series.y0.units)
          }
          series.y0Enabled = true
        }
        hidden = [...obsDataSec]
      }

      // FCST TRANSFORMATION: updated hydrograph logic for 2021-10-27.
      if (stageflow && stageflow.forecast && fcstDataPri.length && stageflow.forecast.primaryUnits === 'ft') {
        // If we don't have OBS data we set the control to FCST settings.
        if (series.y0.units === null) {
          series.y0.units = stageflow.forecast.primaryUnits
          series.y1.units = kcfsToCfsString(
            stageflow.forecast.secondaryUnits,
            (gaugeConfig && gaugeConfig.hydrographNWM) || false
          )
        }
        series.y1.units = stageflow.forecast.secondaryUnits
        series.y0.seriesData.forecast = [...fcstDataSec]
        series.labels.forecast = getLabels(fcstDataPri, series.y0.units)

        // Show the secondary yAxis.
        if (gaugeConfig.hydrographNWM.secondaryY.enabled && fcstDataSec.length && checkRatings(series.ratings)) {
          series.labels.forecast = getLabels(fcstDataPri, series.y0.units, fcstDataSec, series.y1.units)
          series.y0Enabled = true
        } else if (!checkRatings(series.ratings)) {
          series.labels.forecast = getLabels(fcstDataSec, series.y1.units)
        }

        // This sets up the fake yAxis[1] tick positions.
        if (fcstDataSec.length) hidden = [...fcstDataSec]
      } else if (stageflow && stageflow.forecast && fcstDataSec.length) {
        // If we don't have OBS data we set the control to FCST settings.
        if (series.y0.units === null) {
          series.y0.units = stageflow.forecast.secondaryUnits
          series.y1.units = stageflow.forecast.primaryUnits
        }
        series.y1.units = stageflow.forecast.primaryUnits
        series.y1.name = stageflow.forecast.primaryName
        series.y0.seriesData.forecast = [...fcstDataSec]
        series.labels.forecast = getLabels(fcstDataSec, stageflow.forecast.secondaryUnits)

        // Special: we use the ratings table to do a lookup
        // because we do not have a fcstDataPri collection.
        if (gaugeConfig.hydrograph.secondaryY.enabled) {
          if (fcstDataPri.length && !checkRatings(series.ratings)) {
            series.labels.forecast = getLabels(
              fcstDataSec,
              stageflow.forecast.secondaryUnits,
              fcstDataPri,
              stageflow.forecast.primaryUnits,
              kcfsToCfsString(stageflow.forecast.secondaryUnits, (gaugeConfig && gaugeConfig.hydrographNWM) || false)
            )
          } else if (fcstDataPri.length && checkRatings(series.ratings)) {
            let stageOrFlow = stageflow.forecast.secondaryUnits === 'ft' ? 'stage' : 'flow'
            let converted = getDataConverted(fcstDataSec, series.ratings, stageOrFlow)
            series.labels.forecast = getLabels(
              fcstDataSec,
              stageflow.forecast.secondaryUnits,
              converted,
              stageflow.forecast.primaryUnits
            )
            series.y0Enabled = true
          }
        }
      }
    }

    // For National Map and NWM data or no OBS, FCST data available.
    if ((!gauge && series.version.view === 'mapreach') || (series.y0.units === null && series.y1.units === null)) {
      series.y1.name = 'Flow'
      series.y1.units = 'cfs'
    }

    // NWM Data tranformation.
    // Create the series.rawData array of objects for the NWM data.
    // Add the primary and secondary values to series y1 seriesData nwm array.
    // We should have more than one value here since you
    // will have 'reach' key in the series.nwm object.
    if (Object.keys(series.nwm.originals).length >= 1) {
      series.y1.seriesData.nwm = {}
      let newRawData = []

      // Get the time limits configured from CMS.
      let daysBack =
        gaugeConfig && gaugeConfig.hydrographNWM ? Math.abs(gaugeConfig.hydrographNWM.daysBack.value) : null
      let daysAhead =
        gaugeConfig && gaugeConfig.hydrographNWM ? Math.abs(gaugeConfig.hydrographNWM.daysAhead.value) : null

      // We get these values for building on the series.rawData
      // to make sure we don't add a similar { primary, secondary }
      // object to the series.rawData array of objects if already exists.
      let prims = []
      let secs = [...new Set(series.rawData.map((i) => i.secondary))]
      if (checkRatings(series.ratings)) {
        prims = [...new Set(series.rawData.map((i) => i.primary))]
      }

      //This coorelates the gauge.config settings to the NWM trace(s) allowing us to toggle from the cms

      // DISABLING THIS DUE TO DISALLOWANCE OF USERS TO TURN OFF NWM TRACES
      // let configToID = {
      //   analysisAssim: ['analysis_assimilation'],
      //   shortRange: ['short_range'],
      //   longRange: [
      //     'long_range',
      //     'long_range_ensemble_mean',
      //     'long_range_ensemble_member1',
      //     'long_range_ensemble_member1',
      //     'long_range_ensemble_member1',
      //     'long_range_ensemble_member1',
      //   ],
      //   mediumRange: [
      //     'medium_range',
      //     'medium_range_exnemble_mean',
      //     'medium_range_exnemble_member1',
      //     'medium_range_exnemble_member2',
      //     'medium_range_exnemble_member3',
      //     'medium_range_exnemble_member4',
      //     'medium_range_exnemble_member5',
      //     'medium_range_exnemble_member6',
      //   ],
      //   mediumRangeBlend: ['medium_range_blend'],
      // }

      for (const [key, vals] of Object.entries(series.nwm)) {
        // DISABLING THIS DUE TO DISALLOWANCE OF USERS TO TURN OFF NWM TRACES
        //Only show the traces that are toggled to "enabled" in the CMS
        // let useKey = true
        // if (gaugeConfig) {
        //   for (let trace in configToID) {
        //     try {
        //       if (
        //         configToID[trace].includes(key) &&
        //         gaugeConfig.hydrographNWM.series[trace] !== undefined &&
        //         gaugeConfig.hydrographNWM.series[trace].value == false
        //       ) {
        //         useKey = false
        //       }
        //     } catch (error) {
        //       console.error(`Unable to fetch ${trace} from Gauge Config`)
        //       continue
        //     }
        //   }
        // }
        // if (!useKey) continue

        // Don't show anything if data is out of time range unless we are looking at a reach.
        // if (
        //   daysAhead &&
        //   daysBack &&
        //   !dayjs(vals.validTime).isBetween(series.now.subtract(daysBack, 'days'), series.now.add(daysAhead, 'days'))
        // )
        //   continue

        // gaugeConfig global not available with reach data points
        // on the national map.
        //let sfKey = seriesStyle[key] && seriesStyle[key].sfkey
        if (!vals.hasOwnProperty('data')) continue

        // Figure out the precision to deal with gauges like HOPI1.
        let prec = getPrecision(vals.data[0].secondary)
        if (prec > 1) {
          if (prec > 3) {
            series.precision = 3
          } else {
            series.precision = 1
          }
        }

        // We set the default maxPeak array to [-999,-999]
        // because sometimes we have floating numbers
        // and negative numbers to find the peak value on.
        let maxPeak = [-999, -999]
        if (!series.labels[key]) series.labels[key] = {}

        vals.data.forEach((item) => {
          if (isMissing(item.secondary)) return false

          let sec = series.flowUnitsByCMS === 'kcfs' ? cfsToKcfs(item.secondary) : item.secondary

          // Build the NWM data into the series.rawData.
          if (checkRatings(series.ratings)) {
            if (!series.y1.seriesData.nwm[key]) series.y1.seriesData.nwm[key] = []

            // Remember that NWM data is always CFS.
            // We do not send the fourth parameter for ratingsLookup here because
            // the data going in is CFS along with the series ratings.
            let prim = ratingsLookup(series.ratings, item.secondary, 'flow', null, false)

            // If the series.ratings values are limited (like less than 5)
            // then we cannot fully compute the tranformed values.
            if (!isFalsy(prim)) {
              if (prim) prim = Number(prim.toFixed(series.precision))

              // Tooltip labels: Primary will have the CFS value always.
              // Show a dual tooltip label with secondary yAxis enabled.
              series.labels[key][item.x] = `${prim} ${series.y0.units} / ${sec} ${kcfsToCfsString(
                series.y1.units,
                (gaugeConfig && gaugeConfig.hydrographNWM) || false
              )}`
            }
            //If we don't have stage values show labels for flow values
            else {
              series.labels[key][item.x] = `${sec} ${kcfsToCfsString(
                series.y1.units,
                (gaugeConfig && gaugeConfig.hydrographNWM) || false
              )}`
            }

            // This is the real data to be plotted which should be Flow (KCFS or CFS).
            if (!series.ticks.includes(sec)) series.ticks = [...series.ticks, sec]
            series.y1.seriesData.nwm[key] = [...series.y1.seriesData.nwm[key], [item.x, sec]]

            // We do not want duplicates in the series.rawData array of objects.
            // Placement here is important for the loop. Do not move.
            if (!prims.includes(prim) && !secs.includes(sec))
              newRawData = [
                ...newRawData,
                {
                  primary: prim,
                  secondary: sec,
                  type: 'nwm',
                },
              ]
          } else {
            if (!series.y1.seriesData.nwm[key]) series.y1.seriesData.nwm[key] = []

            // Tooltip labels: No secondary yAxis. Show only a single tooltip label.
            series.labels[key][item.x] = `${sec} ${kcfsToCfsString(
              series.y1.units,
              (gaugeConfig && gaugeConfig.hydrographNWM) || false
            )}`

            // This is the real data to be plotted which should be Flow (KCFS or CFS).
            if (!series.ticks.includes(sec)) series.ticks = [...series.ticks, sec]
            series.y1.seriesData.nwm[key] = [...series.y1.seriesData.nwm[key], [item.x, sec]]

            // We do not want duplicates in the series.rawData array of objects.
            // Placement here is important for the loop. Do not move.
            if (!secs.includes(sec)) series.rawData = [...series.rawData, { secondary: sec, type: 'nwm' }]
          }

          // Get the max peak value for this series.
          if (sec && parseFloat(sec) > parseFloat(maxPeak[1])) {
            maxPeak = [item.x, sec]
          }
          // Most important to prevent duplicate rawData values.
          secs = [...secs, sec]
        })
        if (maxPeak != [-999, -999]) series.peaks[key] = maxPeak
      }
      series.rawData = [...series.rawData, ...new Set(newRawData)].sort((a, b) => {
        if (a.primary && a.secondary === b.secondary) {
          return a.primary - b.primary
        }
        return a.secondary - b.secondary
      })
    }

    // Add our stageflows and NWM data to the chart: plot it.
    if (series.y0.seriesData.observed && series.y0.seriesData.observed.length) {
      series.configs = [
        ...series.configs,
        {
          data: series.y0.seriesData.observed.sort((a, b) => (a[0] > b[0] ? 1 : -1)),
          yAxis: 1,
          id: 'observed',
          name: seriesStyle['observed'].name,
          showInNavigator: true,
          color: seriesStyle['observed'].color,
          dataLabels: {
            enabled: true,
            // peak value series object configured.
            formatter: function () {
              if (this.x == series.peaks['observed'][0]) {
                return `${this.y.toFixed(2)} ${kcfsToCfsString(
                  series.y1.units,
                  (gaugeConfig && gaugeConfig.hydrographNWM) || false
                )}`
              }
              return undefined
            },
          },
          marker: {
            enabled: true,
            symbol: 'circle',
            radius: 3,
          },
        },
      ]
    }
    if (series.y0.seriesData.forecast && series.y0.seriesData.forecast.length) {
      series.configs = [
        ...series.configs,
        {
          data: series.y0.seriesData.forecast.sort((a, b) => (a[0] > b[0] ? 1 : -1)),
          yAxis: 1,
          id: 'forecast',
          name: seriesStyle['forecast'].name,
          showInNavigator: true,
          color: seriesStyle['forecast'].color,
          dataLabels: {
            enabled: true,
            // peak value series object configured.
            formatter: function () {
              if (this.x == series.peaks['forecast'][0]) {
                return `${this.y.toFixed(2)} ${kcfsToCfsString(
                  series.y1.units,
                  (gaugeConfig && gaugeConfig.hydrographNWM) || false
                )}`
              }
              return undefined
            },
          },
          marker: {
            enabled: true,
            symbol: 'square',
            radius: 3,
          },
        },
      ]
    }

    // Add NWM data to the chart.
    if (series.y1.seriesData.nwm && Object.keys(series.y1.seriesData.nwm).length) {
      if (!series.y1.units) {
        // If no stageflows but we have NWM data
        // then make sure secondary yAxis has a flow type (KCFS or CFS)
        // based on CMS configured value.
        series.y1.units = series.flowUnitsByCMS
      }
      for (let [key, vals] of Object.entries(series.y1.seriesData.nwm)) {
        // If we don't have ticks because secondary data is undefined or
        // all -999 (invalid values) then we don't want to show any data
        // since we don't have y0 axis tickPositions.
        let units = `${kcfsToCfsString(series.y1.units, (gaugeConfig && gaugeConfig.hydrographNWM) || false)}`
        if (vals && vals.length && series.version.view === 'mapreach') {
          // For NWM only reachID set configuration with National Map on index view.
          series.nwmRawData = [...series.nwmRawData, { primary: series.nwm[key].ymin }]
          series.nwmRawData = [...series.nwmRawData, { primary: series.nwm[key].ymax }]
        }

        // Add NWM data to the chart.
        series.configs = [
          ...series.configs,
          {
            data: vals,
            yAxis: 1,
            id: key,
            name: seriesStyle[key].name,
            showInNavigator: series.version.view === 'mapreach' && series.reachID ? true : false, // On reach only data show in the navigator
            color: seriesStyle[key].color,
            dataLabels: {
              enabled: true,
              // peak value series object configured.
              formatter: function () {
                if (this.x == series.peaks[key][0]) {
                  return `${series.peaks[key][1]} ${units}`
                }
                return undefined
              },
            },
          },
        ]
      }
    }

    // Setup default ticks, rawData
    series.defaultTicks = [...series.ticks]
    series.defaultRawData = [...series.rawData]

    // Reset the ticks to only include headRoom and footRoom by the totalExts.
    // Important to note this handles strange gauges like BIGI1, LCJU1
    let totalExts = getMinMaxSeriesDataYaxis(series.y0.seriesData).filter((i) => isFinite(i) && i != 0)
    series.ticks = adjustYAxisTicks('nwm', series.ticks, series.rawData, totalExts)

    // Add hidden series to series configs.
    if (hidden.length) {
      let hiddenObj = { data: hidden, yAxis: 0, visible: false }
      series.configs = [...series.configs, hiddenObj]
      series.hidden = hidden
    }

    // Initial application of ticks and rawData values.
    series.ticks = [...new Set(series.rawData.map((i) => i.primary))]
    series.rawData = series.rawData.sort((a, b) => {
      if (a.primary && a.secondary === b.secondary) {
        return a.primary - b.primary
      }
      return a.secondary - b.secondary
    })

    // Reach only data (reachID is set but gauge data is not) for National Map view (index view).
    if (series.rawData && !series.rawData.length) {
      series.ticks = getUniqValues(
        series.nwmRawData.sort((a, b) => {
          if (a.secondary && a.primary === b.primary) {
            return a.secondary - b.secondary
          }
          return a.primary - b.primary
        })
      )
    } else {
      series.ticks = getUniqValues(
        series.rawData.sort((a, b) => {
          if (a.secondary && a.primary === b.primary) {
            return a.secondary - b.secondary
          }
          return a.primary - b.primary
        })
      )
    }

    series.ticks = series.ticks.sort((a, b) => parseFloat(a) - parseFloat(b))

    // Create whole numbers values.
    series.wholes = [...new Set(series.rawData.map((i) => i.primary && Math.trunc(i.primary)))]
    // series.ticks = [...new Set(series.rawData.map((i) => stageflow.observed.primaryUnits == 'ft' ? i.secondary : i.primary))]
    // We use checkRatings which is a general function to check for existance and more than 3 elements.
    if (checkRatings(series.ratings) && series.wholes && series.wholes.length == 1) {
      // If small number of whole numbers then "create" some for processing.
      series.wholes = processUnderTwoNumbers(series.wholes)
    } else {
      series.wholes = [...new Set(series.rawData.map((i) => i.secondary && Math.trunc(i.secondary)))]
    }

    // Connect the NWM top range with the bottom range of the
    // stageflows.
    if (
      series.rawData &&
      series.ratings &&
      Object.keys(series.y0.seriesData).length &&
      Object.keys(series.y1.seriesData).length
    ) {
      let stageExtremes = getStageflowExtremes(series.y0.seriesData)
      let bot = Math.ceil(
        ratingsLookup(
          series.ratings,
          stageExtremes.min,
          'flow',
          getFlowUnitsByConfig(gaugeConfig && gaugeConfig.hydrographNWM)
        )
      )

      var streamExtremes = getStreamflowExtremes(series.y1.seriesData)
      let top = Math.floor(
        ratingsLookup(
          series.ratings,
          streamExtremes.max,
          'flow',
          getFlowUnitsByConfig(gaugeConfig && gaugeConfig.hydrographNWM)
        )
      )

      // Fill in the gap between stageflow min and streamflow max
      if (bot > top) {
        for (let p = top; p <= bot - 1; p++) {
          let s = ratingsLookup(series.ratings, p, 'stage')
          if (!isFalsy(s)) {
            s = series.flowUnitsByCMS === 'kcfs' && series.y1.units === 'kcfs' ? cfsToKcfs(s) : s
            series.rawData = [...series.rawData, { primary: p, secondary: s, type: 'added1' }]
          }
        }
      }
    }

    // Sort it all out.
    series.rawData = series.rawData.sort((a, b) => {
      if (a.primary && a.secondary === b.secondary) {
        return a.primary - b.primary
      }
      return a.secondary - b.secondary
    })
    series.ticks = series.ticks.sort((a, b) => a - b)

    //Returns a list of first and last default (SR,MRB,AA) NWM timestamps for x axis range
    function nwmDefaultEnds(series) {
      let nwmSets = []
      if (series.nwm == undefined) {
        return
      }
      if (series.nwm.analysis_assimilation) {
        nwmSets.push(series.nwm.analysis_assimilation.data[0].x)
        nwmSets.push(series.nwm.analysis_assimilation.data.slice(-1)[0].x)
      }
      if (series.nwm.medium_range_blend) {
        nwmSets.push(series.nwm.medium_range_blend.data[0].x)
        nwmSets.push(series.nwm.medium_range_blend.data.slice(-1)[0].x)
      }
      if (series.nwm.short_range) {
        nwmSets.push(series.nwm.short_range.data[0].x)
        nwmSets.push(series.nwm.short_range.data.slice(-1)[0].x)
      }
      return nwmSets
    }

    //Set x Axis range based on Days ahead/back
    if (series.dataExists) {
      series.extremes['xaxis'] = []
      let back = 7
      let ahead = 7
      if (gaugeConfig) {
        let maxNWM = Math.max(...nwmDefaultEnds(series))
        let minNWM = Math.min(...nwmDefaultEnds(series))
        //Set back to furthest past point if -1 or CMS value if not
        back =
          gaugeConfig.hydrographNWM.daysBack.value == -1 && (series.obsDataPri.length || series.obsDataSec.length)
            ? Math.ceil(
                Math.abs(
                  series.now -
                    new Date(
                      series.obsDataPri.length && series.obsDataSec.length
                        ? Math.min(series.obsDataPri[0][0], series.obsDataSec[0][0])
                        : series.obsDataPri.length
                          ? series.obsDataPri[0][0]
                          : series.obsDataSec[0][0]
                    )
                ) / 86400000
              )
            : Math.abs(gaugeConfig.hydrographNWM.daysBack.value)
        //Check if NWM is further back than obs data. Max because absolute values
        if (gaugeConfig.hydrographNWM.daysBack.value == -1 && !isNaN(minNWM) && Math.abs(minNWM) != Infinity) {
          back = Math.max(back, (series.now - new Date(Math.abs(minNWM))) / 86400000)
        }

        //Set ahead either to furthest future point if -1 or CMS value if not
        ahead =
          gaugeConfig.hydrographNWM.daysAhead.value == -1 && (series.fcstDataPri.length || series.fcstDataSec.length)
            ? Math.ceil(
                Math.abs(
                  series.now -
                    new Date(
                      series.fcstDataPri.length && series.fcstDataSec.length
                        ? Math.max(series.fcstDataPri.slice(-1)[0][0], series.fcstDataSec.slice(-1)[0][0])
                        : series.fcstDataPri.length
                          ? series.fcstDataPri.slice(-1)[0][0]
                          : series.fcstDataSec.slice(-1)[0][0]
                    )
                ) / 86400000
              )
            : Math.abs(gaugeConfig.hydrographNWM.daysAhead.value)
        // Check if NWM is further ahead than fcst data or default. Max because absolute values
        if (gaugeConfig.hydrographNWM.daysBack.value == -1 && !isNaN(maxNWM) && Math.abs(maxNWM) != Infinity) {
          ahead = Math.max(ahead, (series.now - new Date(Math.abs(maxNWM))) / 86400000)
        }
      }
      series.extremes.xaxis[0] = getXAxisExtremes(series.dataExists, gauge, back, ahead)

      // Get the yAxis extremes for y0 axis.
      series.extremes.yaxis[1] = getYAxisExtremes(series)
      var trueMin
      var trueMax

      if (streamExtremes && streamExtremes.min < series.extremes.yaxis[1].min) {
        trueMin = streamExtremes.min
      } else {
        trueMin = series.extremes.yaxis[1].min
      }

      if (streamExtremes && streamExtremes.max > series.extremes.yaxis[1].max) {
        trueMax = streamExtremes.max
      } else {
        trueMax = series.extremes.yaxis[1].max
      }
      // headroom-footroom set by CMS in flow units for Gauge page NWM HGs
      if (gaugeConfig) {
        if (gaugeConfig.hydrographNWM.headRoom.value) {
          series.headRoom = Math.abs(gaugeConfig.hydrographNWM.headRoom.value)
        }
        if (gaugeConfig.hydrographNWM.footRoom.value) {
          series.footRoom = Math.abs(gaugeConfig.hydrographNWM.footRoom.value)
        }
      }
      //Default 20% headroom and 10% footroom for Reach NWM HGs
      else {
        series.headRoom = (trueMax - trueMin) * 0.2
        series.footRoom = (trueMax - trueMin) * 0.1
      }
      let top = trueMax + series.headRoom
      series.extremes.yaxis[1].max = top
      series.wholes = [...series.wholes, top]
      series.ticks = [...series.ticks, top]
      let bottom = trueMin - series.footRoom
      series.extremes.yaxis[1].min = bottom
      series.wholes = [bottom, ...series.wholes]
      series.ticks = [bottom, ...series.ticks]

      //Recalculate evenly distributed ticks with new top and bottom
      series.ticks = calculateTicks(series.ticks, false, series.precision)

      // Ingest the crests data into the series object.
      series['crests'] = parseCrestsData(series, gauge)

      // make sure to delete this array after we get our big series
      // Streamflow data
      series['record_stageline'] = getRecordStageLine(series, gauge, stageflow)

      // Get flood categories
      series['floodcats'] = parseFloodCats(series, gaugeConfig)
      series['plotbands'] = getFloodLevelsConfig(series, gauge, stageflow, gaugeConfig)
      series['plotbandlines'] = getGaugeStageFlowLinesLabels(series, gaugeConfig)

      // Get Normal Pool Threshhold
      series['normal_pool'] = getNormalPoolFloodThreshHold(gaugeConfig, series.gauge)
      if (series.plotbands && series.normal_pool) {
        // Create the label for the legend button.
        series['normal_pool_toggle'] = getNormalPoolFloodThreshHoldConfig(series.normal_pool)
        if (series.normal_pool_toggle) series.configs = [...series.configs, series.normal_pool_toggle]
        series.plotbands = [...series.plotbands, series.normal_pool]
      }

      if (series.plotbandlines && series.record_stageline && series.record_stageline.length) {
        series.plotbandlines = [...series.plotbandlines, series.record_stageline]
      }

      series['record_stageline_toggle'] = getRecordStageLineConfig(series)
      if (
        series.record_stageline_toggle &&
        series.record_stageline &&
        series.plotbands &&
        series.plotbandlines &&
        !isFalsy(series.record_stageline.value)
      ) {
        series.configs = [...series.configs, series.record_stageline_toggle]
        series.plotbandlines = [...series.plotbandlines, series.record_stageline_toggle]
        series.plotbands = [...series.plotbands, series.record_stageline]
      }

      // Ingest low water data into the series object.
      series['lowwater'] = parseLowWaterData(gauge)
      series['lowwater_stageline'] = getLowWaterStageLine(gaugeConfig, series.version.chart)
      if (series.plotbandlines && series.lowwater && series.lowwater_stageline && series.lowwater_stageline.value) {
        let lowwaterMin =
          series.extremes.yaxis[0].min < series.lowwater_stageline.value
            ? series.extremes.yaxis[0].min
            : series.lowwater_stageline.value

        series['lowwater_shading'] = addLowwaterShading(series, lowwaterMin, series.lowwater_stageline.value)
        let text = series.lowwater_stageline.label.text
        if (series.lowwater_stageline.unit === 'ft') text = `Low Stage: ${series.lowwater_stageline.value} ft`
        let lowwater = {
          id: series.lowwater_stageline.id,
          name: text,
          color: seriesStyle['lowwater_stageline'].color,
          marker: seriesStyle['lowwater_stageline'].marker,
          value: series.lowwater_stageline.value,
          y: 0,
        }
        series.configs = [...series.configs, lowwater]
        if (Object.keys(series.lowwater_stageline))
          series.plotbandlines = [...series.plotbandlines, series.lowwater_stageline]
      }

      if (series.plotbands && series.plotbands.length && series.lowwater_shading) {
        series['plotbands'].push(series.lowwater_shading)
      }

      // Assign the plotbands object to chart series configuration.
      if (series.plotbands && series.plotbands.length) {
        let d = getPlotLinesFlowBandsConfig(series)
        if (d) series.configs = [...series.configs, d]
      }

      // Add yAxis extremes
      // Latest Observed Value MessageBox
      series['latest_observed'] = { msg: '' }
      series.latest_observed.msg = getLatestObservedValues(series, gaugeConfig)
    } else {
      // TODO: fix this to not fall back to UTC. TM
      gauge = { lid: '', timeZone: 'UTC' }
      series = { dataExists: false, timeZone: 'UTC' }
    }

    const gaugeLabelsXaxis = function () {
      return Highcharts.dateFormat('%l %P<br>%b %e', dayjs(this.value).valueOf())
    }

    // Adjust if we only have a single series.rawData object.
    // We need at least 3 objects in the series.rawData.
    if (series.rawData && series.rawData.length && series.rawData.length <= 2 && series.ticks && series.ticks.length) {
      let newRawData = []
      let newTicks = []
      let curr = Math.round(series.rawData[0].secondary)
      let bot = Number((curr - 1).toFixed(series.precision))
      let top = Number((curr + 1).toFixed(series.precision))
      newTicks = [bot, ...series.ticks, top]
      newTicks = calculateTicks(newTicks, false, series.precision)

      if (checkRatings(series.ratings)) {
        newTicks.forEach((i) => {
          let s = ratingsLookup(
            series.ratings,
            i,
            'flow',
            getFlowUnitsByConfig(gaugeConfig && gaugeConfig.hydrographNWM)
          )
          if (s && !isFalsy(s)) {
            newRawData = [
              ...newRawData,
              { primary: Number(s.toFixed(series.precision)), secondary: i, type: 'adjustment' },
            ]
          }
        })
      } else {
        newRawData = [
          { secondary: bot, type: 'adjustment' },
          { secondary: top, type: 'adjustment' },
        ]
      }
      series.ticks = [...newTicks].sort((a, b) => a - b)
      series.rawData = [...series.rawData, ...newRawData].sort((a, b) => {
        if (a.primary && a.secondary === b.secondary) {
          return a.primary - b.primary
        }
        return a.secondary - b.secondary
      })
    }

    // Try to fill in the gap between stageflow and streamflow if there is one.
    let stageExt = getStageflowExtremes(series, 'y0')
    let streamExt = getStreamflowExtremes(series, 'y1')
    //console.debug(`stageExt: `, stageExt, `, streamExt: `, streamExt)

    if (!isFalsy(stageExt.max) && !isFalsy(streamExt.min) && streamExt.max < stageExt.min) {
      let gapDiff, from, to
      if (streamExt.max < stageExt.min) {
        from = streamExt.min
        to = stageExt.min
        gapDiff = Math.floor(stageExt.min - streamExt.max)
      } else if (stageExt.max < streamExt.min) {
        from = stageExt.max
        to = streamExt.min
        gapDiff = Math.floor(streamExt.min - stageExt.max)
      }
      //console.debug(`gapDiff :${gapDiff}:`)

      if (gapDiff && gapDiff > 50) {
        for (let i = from; i <= to; i += 50) {
          if (checkRatings(series.ratings)) {
            if (i >= series.extremes.ratings.min && i <= series.extremes.ratings.max) {
              let p = ratingsLookup(
                series.ratings,
                i,
                'flow',
                getFlowUnitsByConfig(gaugeConfig && gaugeConfig.hydrographNWM)
              )
              series.rawData = [
                ...series.rawData,
                {
                  primary: p,
                  secondary: i,
                  type: 'gapfiller1',
                },
              ]
            }
          } else {
            series.rawData = [
              ...series.rawData,
              {
                secondary: i,
                type: 'gapfiller2',
              },
            ]
          }
        }
      }
    }
    // series.ticks = calculateTicks(series.ticks.sort(), false, series.precision)
    // Final update and sort.
    series.rawData = sortRawDataSecondaryPrimary(series)

    //Create cooresponding y0 ticks
    const y0Ticks = (series) => {
      if (!series.ticks && !series.ticks.length) {
        return []
      }
      let ticks = []
      for (let i = 0; i < series.ticks.length; i++) {
        let temp = ratingsLookup(series.ratings, series.ticks[i], 'flow', series.flowUnitsByCMS)
        if (!isNaN(temp) || temp > -999) {
          ticks = [...ticks, temp]
        }
      }
      return ticks
    }

    // Handle view static hydrograph context menu item if gauge else don't show it. MS
    let menuButtonsArray = [
      'viewFullscreen',
      'printChart',
      'separator',
      'downloadPNG',
      'downloadJPEG',
      'downloadPDF',
      'downloadSVG',
      'static',
      'tabularData',
    ]
    if (!gauge) {
      menuButtonsArray = [
        'viewFullscreen',
        'printChart',
        'separator',
        'downloadPNG',
        'downloadJPEG',
        'downloadPDF',
        'downloadSVG',
      ]
    }

    // Chart Config (series.series_config)
    let opts = {
      chart: {
        name: 'nwm',
        spacingRight: 0,
        renderTo: renderTo,
        backgroundColor: chartBgColor,
        height: '600px',
        type: 'line',
        marginTop: 80,
        scrollablePlotArea: true,
        zoomType: 'xy',
        events: {
          redraw: function () {
            // remove svg rendered observation-forecast topbar
            document.querySelectorAll('.nwsRenderNWM').forEach((i) => i.remove())
            obsForecastTopBar(this, series)

            document.querySelectorAll('.metadataLabelNWM').forEach((i) => i.remove())
            getMetaDataSubHeading(this, series, gaugeConfig ? gaugeConfig : null)

            // If no data (series) like gauge MIAM7 then
            // remove currentDateTimeLine, etc.
            chartHasNoData(this, series)

            // Update the last OBS box x, y on chart redraw so it stays in same spot.
            if (this.latestObservedBox) {
              let chartPosition = getChartPositionsByData(chart)
              this.latestObservedBox.attr(chartPosition)
            }
            // Update the watermark x, y on chart redraw so it stays on the "forecast" side.
            if (this.watermark) {
              chartWaterMark(this, null, series.now)
            }
            imageResize(this)

            // Iterate the tick marks and hide the one that will overlap. TM
            // TODO: clean this function up.
            preventYaxisTickOverlap(this)
          },
          resize: function () {
            // remove svg rendered observation-forecast topbar
            document.querySelectorAll('.nwsRenderNWM').forEach((i) => i.remove())
            obsForecastTopBar(this, series)

            document.querySelectorAll('.metadataLabelNWM').forEach((i) => i.remove())
            getMetaDataSubHeading(this, series, gaugeConfig ? gaugeConfig : null)
          },
          load: function (e) {
            obsForecastTopBar(this, series)
            getMetaDataSubHeading(this, series, gaugeConfig ? gaugeConfig : null)
            // Commenting this out in case IWT wants it back in
            chartWaterMark(this, 'Model Guidance<br>Not Official Forecast', series.now)
            latestObservedBox(this, series)
            addNOAAWaterMark(this)
            preventYaxisTickOverlap(this)

            // Set default view to 7 days. Commented out on 2023-11-27 since this is controlled by the CMS. MS
            //this.xAxis[0].setExtremes(series.now.subtract(84, 'hour').valueOf(), series.now.add(84, 'hour').valueOf())
          },
        },
      },
      // https://www.highcharts.com/forum/viewtopic.php?t=34929
      navigation: {
        buttonOptions: {
          margin: '2px',
          theme: {
            //fill: chartBgColor,
            //states: {
            //    hover: {
            //    fill: (chartBgColor == '#FFFFFF' ? '#CDCDCD' : '#FFFFFF')
            //    }
            //}
          },
        },
      },
      navigator: {
        enabled: true,
        xAxis: {
          labels: {
            style: {
              color: '#666666',
            },
          },
        },
      },
      rangeSelector: {
        verticalAlign: 'bottom',
        // By default buttons are only enabled if the corresponding time range exists on the X axis. TM
        allButtonsEnabled: true,
        buttons: [
          {
            type: 'hour',
            count: 24,
            text: '1d',
            events: {
              click: function () {
                chart.xAxis[0].setExtremes(
                  series.now.subtract(12, 'hour').valueOf(),
                  series.now.add(12, 'hour').valueOf()
                )
                return false
              },
            },
          },
          {
            type: 'hour',
            count: 48,
            text: '2d',
            events: {
              click: function () {
                chart.xAxis[0].setExtremes(
                  series.now.subtract(24, 'hour').valueOf(),
                  series.now.add(24, 'hour').valueOf()
                )
                return false
              },
            },
          },
          {
            type: 'hour',
            count: 168,
            text: '7d',
            events: {
              click: function () {
                chart.xAxis[0].setExtremes(
                  series.now.subtract(84, 'hour').valueOf(),
                  series.now.add(84, 'hour').valueOf()
                )
                return false
              },
            },
          },
          {
            type: 'week',
            count: 2,
            text: '14d',
            events: {
              click: function () {
                chart.xAxis[0].setExtremes(
                  series.now.subtract(168, 'hour').valueOf(),
                  series.now.add(168, 'hour').valueOf()
                )
                return false
              },
            },
          },
          {
            type: 'ytd',
            text: 'All',
            tooltip: 'All Available Data',
            events: {
              click: function () {
                var extremes = chart.xAxis[0].getExtremes()
                chart.xAxis[0].setExtremes(extremes.dataMin, extremes.dataMax)
                return true
              },
            },
          },
        ],
      },
      legend: {
        enabled: false,
      },
      noData: {
        title: {
          title: 'No Data Found',
        },
        style: {
          fontWeight: 'bold',
          fontSize: '28px',
          color: '#ff0000', // red
        },
      },
      title: {
        text: gaugeConfig
          ? gaugeConfig.hydrographNWM.nameOveride.enabled
            ? gaugeConfig.hydrographNWM.nameOveride.value
            : getTitleText(series)
          : getTitleText(series),
      },

      credits: {
        enabled: false,
      },

      subtitle: {
        text:
          series.ratings == null
            ? `Reach ID: ${reachID}`
            : `NWSLI: ${series.dataExists && series.gauge ? gauge.lid.toUpperCase() : ''}, Reach ID: ${reachID}`,

        marginBottom: '250px',
      },

      xAxis: {
        gridLineWidth: 0.2,
        gridLineColor: '#b8b5bc',
        title: {
          text: series && series.gauge ? `Site Time (${series.timeZoneAbbr})` : '',
          style: {
            fontSize: '14px',
            fontWeight: 'bold',
          },
        },
        ordinal: false,
        paddingLeft: 20,
        type: 'datetime',
        min: series.extremes && series.extremes.xaxis ? series.extremes.xaxis[0].min : null,
        max: series.extremes && series.extremes.xaxis ? series.extremes.xaxis[0].max : null,
        minTickInterval: 1 * 3600 * 1000, // 1 hour interval
        //maxtickInterval: 24 * 3600 * 1000,    // 24 hour interval
        labels: {
          formatter: gaugeLabelsXaxis,
          step: 2,
          staggerLines: 1,
        },
        plotLines: [
          {
            id: 'currentDateTimeLineNWM',
            class: 'current-datetime-line-nwm',
            color: '#1d89cb', // Per Nurture Nature graphic. TM
            dashStyle: 'dash', // Per Nurture Nature graphic. TM
            value: series.dataExists ? series.now : null,
            width: '2',
            zIndex: 4,
          },
        ],
        // Observed Shading using xaxis plotband
        plotBands: [
          {
            id: 'obsShadingNWM',
            class: 'obs-shading-nwm',
            from: 0,
            to: dayjs(),
            color: '#f0f0f0',
            opacity: 0.1,
          },
        ],
      },

      exporting: {
        filename: getTitleText(series),
        enabled: true,
        sourceWidth: 1600,
        sourceHeight: 800,
        scale: 1,
        menuItemDefinitions: {
          // Custom definition
          static: {
            onclick: function () {
              return window.open(`${OWP_WEB_URL}/resources/hydrographs/${series.gauge.lid}_hg.png`)
            },
            text: 'View Static Hydrograph',
          },
          tabularData: {
            onclick: function () {
              return window.open(`${OWP_WEB_URL}/gauges/${gauge.lid}/tabular`)
            },
            text: 'View Tabular Data',
          },
        },
        buttons: {
          contextButton: {
            menuItems: menuButtonsArray,
          },
        },
      },

      yAxis: [
        {
          name: 'y0',
          opposite: false,
          startOnTick: true,
          showLastLabel: true,
          ordinal: false,
          endOnTick: true,
          title: {
            //If Y0 is enabled use title from primaryName in API or default to stage, otherwise set as blank
            text: series.y0Enabled
              ? stageflow && stageflow.observed.primaryName
                ? stageflow.observed.primaryUnits == 'ft'
                  ? stageflow.observed.primaryName + ' (FT)'
                  : stageflow.observed.secondaryName + ' (FT)'
                : 'Stage (FT)'
              : '',
            enabled: series.y0Enabled,
            style: {
              color: '#000',
              fontWeight: 'bold',
              fontSize: '14px',
            },
          },
          labels: {
            // Custom y1Axis labels to FAKE the display of SECONDARY (CFS) and maintain the
            // association to PRIMARY data (FT)
            reserveSpace: true,
            enabled: series.y0Enabled,
            align: 'right',
            formatter: function () {
              if (!y0Ticks || y0Ticks == undefined || !Array.isArray(y0Ticks)) return undefined
              let first = y0Ticks[0]
              y0Ticks.shift()
              if (first == '') {
                return first
              }
              return toFixedPos(first.toFixed(2), 2)
            },
          },
          // Use our custom defined ticks for y0Axis
          tickPositioner: function () {
            return series.ticks
          },
          events: {
            afterSetExtremes: function () {
              this.chart.yAxis[1].update()
            },
          },
        },
        {
          name: 'y1',
          startOnTick: true,
          showLastLabel: true,
          ordinal: false,
          endOnTick: true,
          title: {
            text:
              series && series.y1 && series.y1.units
                ? `${series.y1.name} (${kcfsToCfsString(
                    series.y1.units,
                    (gaugeConfig && gaugeConfig.hydrographNWM) || false
                  ).toUpperCase()})`
                : '',
            enabled: series && series.y1 && series.y1.units ? true : false,
            style: {
              color: '#000',
              fontWeight: 'bold',
              fontSize: '14px',
            },
          },
          opposite: true,
          // We have to MATCH y0Axis ticks EXACTLY in order for this to work and display the
          // nonlinear FT <--> CFS  association
          tickPositioner: function () {
            return series.ticks
          },
          labels: {
            align: 'right',
            reserveSpace: true,
            formatter: function () {
              // This will prevent rounding up of secondary value
              // and only showing 1 decimal place.
              //return this.value
              return this.value == 0 ? 0.0 : toFixedPos(this.value.toFixed(2), 2)
            },
          },
          plotBands: series.plotbands,
          plotLines: series.plotbandlines,
        },
      ],

      plotOptions: {
        series: {
          // Custom variables for the chart: https://api.highcharts.com/highcharts/plotOptions.series.custom
          custom: {
            chartBackgroundColor: '#FFFFFF',
            peak: {},
          },
        },
      },

      tooltip: {
        backgroundColor: '#999',
        useHTML: true,
        borderWidth: 2,
        borderRadius: 5,
        borderColor: '#000',
        shadow: false,
        split: true,
        formatter: function () {
          // Formats the tooltip data points for forecast, observed, shortrange, analysis assim
          let d = dayjs(this.x)
          // Default tooltip that apears on xAxis
          let tt = [
            `<div style="color: #000; background-color: #999; padding: 5px;">${d.format(
              'ddd MMM D, YYYY h:mm:ss A z'
            )}</div>`,
          ]

          this.points.forEach((point, key) => {
            let thisSeries = point.series,
              label = ''
            let readable = '#000000',
              id = thisSeries.userOptions.id

            try {
              switch (id) {
                case 'forecast':
                case 'observed':
                  readable = '#ffffff'
                  label = series.labels[id] ? series.labels[id][point.x] : ''
                  break
                case 'long_range_ensemble_mean':
                case 'medium_range_ensemble_mean':
                  readable = '#ffffff'
                  label = series.labels[id] ? series.labels[id][point.x] : ''
                  break
                case 'medium_range_ensemble_member1':
                case 'medium_range_ensemble_member2':
                case 'medium_range_ensemble_member3':
                case 'medium_range_ensemble_member4':
                case 'medium_range_ensemble_member5':
                case 'medium_range_ensemble_member6':
                case 'medium_range_ensemble_member7':
                case 'medium_range_ensemble_member8':
                case 'long_range_ensemble_member1':
                case 'long_range_ensemble_member2':
                case 'long_range_ensemble_member3':
                case 'long_range_ensemble_member4':
                  label = series.labels[id] ? series.labels[id][point.x] : ''
                  break
                case 'analysis_assimilation':
                case 'short_range':
                case 'long_range':
                case 'medium_range_blend':
                case 'medium_range':
                  label = series.labels[id] ? series.labels[id][point.x] : ''
                  break
              }
              tt.push(
                `<div class="tickMarkToolTip" style="color: ${readable}; background-color: ${
                  thisSeries.userOptions.color
                }; padding: 0px;">${shortToolTips[thisSeries.name]}: ${label} </div>`
              )
            } catch (err) {
              console.error(`ERROR hydrographNWM tooltips: `, err)
            }
          })
          return tt
        },
      },
      series: series.configs,
    } // end chart config

    // Instantiate chart
    chart = Highcharts.stockChart(opts)

    // Make chart adjustments: this is done here because in the event.load it was
    // causing a conflict with the 'afterSetExtremes' and yAxis[1]
    //adjustXaxisHydrographNWM(chart, series)

    /* Reflows the chart to its container. However, there are no reliable
    events for div resize, so if the container is resized without a
    window resize event, this must be called explicitly. Have to set this
    in a setTimeout to make sure the element is added to the DOM first. TM*/
    // setTimeout(() => {
    //   chart.reflow()
    // }, 100)

    // If no data (series) like gauge MIAM7 then
    // remove currentDateTimeLine, etc.
    chartHasNoData(chart, series)

    // This is how you would add headroom or footroom to scale.
    // Tested this but seeing oddities in the chart.
    //ticks.unshift(ticks[0] - 5.5)
    //ticks.push(ticks[ticks.length - 1] + 5.5)

    if (DEBUG && env == 'ons-dev') {
      console.debug(
        `============================= chart: NWM for ` +
          (gauge && gauge.lid ? `Gauge ${gauge.lid}` : `Reach ${reachID}`) +
          ` =============================`
      )

      // Backend data from the CMS.
      console.debug(`gauge: `, gauge)

      // Backend data from the CMS.
      console.debug(`gaugeConfig: `, gaugeConfig)

      // The options configuration for this highcharts graph.
      console.debug(`opts: `, opts)

      // Chart object after instantiation.
      console.debug(`nwm chart object: `, chart)

      // The custom global variables of the hydrograph.
      console.debug(`series: `, series)

      console.debug(`UAT-push version: @@1.1`) // Non-damaging way to help see new changes on UAT with fast-scp method. (cat and find with bundle.js.map @@ shows up)
    }
  })

  let myPromisePercentComplete = 0

  $: myPromisePercentComplete =
    (loadingStatus['analysis_assimilation'] != 0 ? 19 : 0) +
    (loadingStatus['short_range'] != 0 ? 19 : 0) +
    (loadingStatus['long_range'] != 0 ? 19 : 0) +
    (loadingStatus['medium_range'] != 0 ? 19 : 0) +
    (loadingStatus['medium_range_blend'] != 0 ? 19 : 0)
</script>

{#if series.ratings === null}
  <div class="uk-text-danger uk-text-center">
    Warning: no valid ratings curve available. NWM transformations to and from FEET/CFS/KCFS will not happen.
  </div>
{/if}
{#if series.observed === null && series.forecast === null}
  <div class="uk-text-danger uk-text-center">Warning: no valid observation, forecast available.</div>
{/if}
{#if series.y0Enabled === false}
  <div class="uk-text-danger uk-text-center">
    Note: A stage-discharge relationship does not exist or is not available.
  </div>
{/if}
<div id={renderTo} />

{#await promiseWait}
  Loading ({myPromisePercentComplete}%)...
{:then}
  {#if view === 'gauge'}
    <div>
      <label
        ><input
          type="checkbox"
          id="auto-refresh-hg"
          class="uk-margin-left uk-checkbox"
          uk-tooltip="Refreshes Hydrograph data every 5 minutes"
          bind:checked={$refresh}
          on:click={() => {
            refresh.set(!$refresh)
          }}
        />&nbsp;Auto Refresh</label
      >
    </div>
  {/if}
  <div id="legend">
    <!-- {#if gaugeConfig != null} -->
    <Legend
      {assetURL}
      floodcategories={series.floodcategories}
      {gauge}
      {series}
      {shortLegend}
      streamflows={getLegendSections(chart.series, streamflows)}
      stageflows={getLegendSections(chart.series, stageflows)}
      {chart}
      floodCatUnits={gaugeConfig && gaugeConfig.floodCatUnits && series.y0Enabled ? gaugeConfig.floodCatUnits : null}
    />
    <!-- {/if} -->
  </div>
{/await}

<style>
  #legend {
    margin-bottom: 1rem;
  }
</style>
